import type { DealHistory, StateTree } from '@makemydeal/dr-dash-types';
import type { DetailedBreakdown, DetailedBreakdownItem, ItemizedDiscount } from '@makemydeal/dr-platform-types';
import { DiscountNames } from '@makemydeal/dr-platform-types';
import { createSelector } from 'reselect';
import { getEffectiveDiscount } from './dealEffectiveSelectors';
import { getDiscount, getOfferDealerDiscounts, getOfferDealerDiscountsTotalAmount } from './offerRedux';

export const transformDealerDiscounts = (dealerDiscounts: ItemizedDiscount[]): DetailedBreakdownItem[] => {
    return dealerDiscounts.map((discount: ItemizedDiscount) => {
        return { name: discount.discountName, value: discount.discountAmount };
    });
};

export const getMsrpDiscount = (state: StateTree, deal?: DealHistory): number => {
    return deal ? getEffectiveDiscount(state, deal) : getDiscount(state);
};

export const getDealDealerDiscountsTotalAmount = (state: StateTree, deal?: DealHistory): number => {
    return deal ? getOfferDealerDiscountsTotalAmount(deal) : getOfferDealerDiscountsTotalAmount(state);
};

export const getDealDealerDiscounts = (state: StateTree, deal?: DealHistory): ItemizedDiscount[] => {
    return deal ? getOfferDealerDiscounts(deal) : getOfferDealerDiscounts(state);
};

export const isTotalDiscountsPositive = createSelector(
    [
        (state: StateTree, deal?: DealHistory) => getMsrpDiscount(state, deal),
        (state: StateTree, deal?: DealHistory) => getDealDealerDiscountsTotalAmount(state, deal)
    ],
    (msrpDiscount: number, dealerDiscountsTotalAmount: number | null): boolean => {
        const totalDealerDiscount = dealerDiscountsTotalAmount ?? 0;
        return msrpDiscount > totalDealerDiscount;
    }
);

export const getDealerDiscountsLoggingContext = (state: StateTree, deal?: DealHistory) => {
    return {
        dealerDiscounts: getDealDealerDiscounts(state, deal),
        dealerDiscountsTotalAmount: getDealDealerDiscountsTotalAmount(state, deal)
    };
};

export const getDealerAdjustment = createSelector(
    [
        (state: StateTree, deal?: DealHistory) => getMsrpDiscount(state, deal),
        (state: StateTree, deal?: DealHistory) => getDealDealerDiscountsTotalAmount(state, deal)
    ],
    (msrpDiscount: number, dealerDiscountsTotalAmount: number | null): number => {
        const totalDealerDiscount = dealerDiscountsTotalAmount ?? 0;
        return msrpDiscount - totalDealerDiscount;
    }
);

export const getDiscountsDifference = createSelector(
    [
        (state: StateTree, deal?: DealHistory) => getDealerAdjustment(state, deal),
        (state: StateTree, deal?: DealHistory) => getDealDealerDiscountsTotalAmount(state, deal)
    ],
    (discountsDifference: number, dealerDiscountsTotal: number | null): number => {
        const totalDealerDiscount = dealerDiscountsTotal ?? 0;
        return discountsDifference > 0 ? discountsDifference : totalDealerDiscount;
    }
);

export const getDealerAdjustmentTotal = createSelector(
    [
        (state: StateTree, deal?: DealHistory) => getMsrpDiscount(state, deal),
        (state: StateTree, deal?: DealHistory) => isTotalDiscountsPositive(state, deal),
        (state: StateTree, deal?: DealHistory) => getDiscountsDifference(state, deal)
    ],
    (msrpDiscount: number, isTotalDiscountsPositive: boolean, totalDiscounts: number): number => {
        const _baseDiscount = msrpDiscount ?? 0;

        return isTotalDiscountsPositive ? totalDiscounts : 0;
    }
);

export const getSummaryDiscountTotalLabel = createSelector(
    [
        (state: StateTree, deal?: DealHistory) => getMsrpDiscount(state, deal),
        (state: StateTree, deal?: DealHistory) => getDealDealerDiscountsTotalAmount(state, deal)
    ],
    (msrpDiscount: number, dealerAdjustment: number): number | undefined => {
        return msrpDiscount > 0 ? msrpDiscount : dealerAdjustment;
    }
);

export const getSummaryDiscount = createSelector(
    [
        (state: StateTree, deal?: DealHistory) => getMsrpDiscount(state, deal),
        (state: StateTree, deal?: DealHistory) => getDealerAdjustment(state, deal),
        (state: StateTree, deal?: DealHistory) => getDealDealerDiscountsTotalAmount(state, deal)
    ],
    (msrpDiscount: number, dealerAdjustment: number, _dealerDiscounts: number | null): number | undefined => {
        if (!msrpDiscount) return 0;
        if (msrpDiscount < 0 || dealerAdjustment < 0) return dealerAdjustment;
        return undefined;
    }
);

export const getSummaryDealerDiscounts = createSelector(
    [
        (state: StateTree, deal?: DealHistory) => getDealDealerDiscounts(state, deal),
        (state: StateTree, deal?: DealHistory) => getDealerAdjustmentTotal(state, deal)
    ],
    (dealerDiscounts: ItemizedDiscount[], dealerAdjustmentTotal: number): DetailedBreakdown | undefined => {
        const items: DetailedBreakdownItem[] = transformDealerDiscounts(dealerDiscounts ?? []);

        if (dealerAdjustmentTotal > 0) {
            items.push({
                name: DiscountNames.DEALER_ADJUSTMENT,
                value: dealerAdjustmentTotal
            });
        }

        const discountsTotal = items.reduce((sum, item) => sum + (item.value ?? 0), 0);

        return discountsTotal
            ? {
                  value: discountsTotal,
                  items
              }
            : undefined;
    }
);

export const getSummaryDealerDiscountsForPrinting = createSelector(
    [(state: StateTree, deal?: DealHistory) => getMsrpDiscount(state, deal)],
    (msrpDiscount: number): { value: number } | undefined => {
        return msrpDiscount ? { value: msrpDiscount } : undefined;
    }
);
